import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Empty, Select, Space, Table, Tag } from 'antd';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import { ProjectSelector } from '@/components/project/ProjectSelector';
import pmvFieldOpsService from '@/services/pmv-field-ops.service';
import { useProjects } from '@/hooks/useProjects';
import { buildProjectIdToLabelMap, resolveProjectDisplayFromMap } from '@/utils/pmvDisplay';
import { hireTableCellEllipsis, hireTableLinkBtnStyle } from '@/utils/pmvHireUi';

/** Field Ops exceptions — returned sheets + incomplete envelopes. */
const PmvFieldOpsExceptionsPage: React.FC = () => {
  const navigate = useNavigate();
  const [kind, setKind] = useState<string | undefined>();
  const [projectId, setProjectId] = useState<string | undefined>();
  const { data: projects = [] } = useProjects();
  const projectLabels = useMemo(() => buildProjectIdToLabelMap(projects), [projects]);

  const returnedQ = useQuery({
    queryKey: ['pmv-sheet-exceptions-returned', projectId],
    queryFn: () =>
      pmvFieldOpsService.listSheets({
        status: 'RETURNED',
        pageSize: 100,
        project_id: projectId,
      }),
  });
  const progressQ = useQuery({
    queryKey: ['pmv-sheet-exceptions-progress', projectId],
    queryFn: () =>
      pmvFieldOpsService.listSheets({
        status: 'IN_PROGRESS',
        pageSize: 100,
        project_id: projectId,
      }),
  });

  const rows = useMemo(() => {
    const all = [
      ...(returnedQ.data?.data ?? []).map((r) => ({ ...r, _kind: 'RETURNED' as const })),
      ...(progressQ.data?.data ?? []).map((r) => ({ ...r, _kind: 'IN_PROGRESS' as const })),
    ];
    if (!kind) return all;
    return all.filter((r) => r._kind === kind);
  }, [returnedQ.data, progressQ.data, kind]);

  const loading = returnedQ.isLoading || progressQ.isLoading;

  return (
    <WorkspaceLayout>
      <EnterprisePageHeader
        documentLabel="Field Ops Exceptions"
        subtitle="Returned / in-progress sheets requiring supervisor or site attention"
        primaryActions={
          <Space wrap>
            <Button type="primary" onClick={() => navigate('/asset/pmv/daily-log-sheets')}>
              Sheets
            </Button>
            <Button onClick={() => navigate('/asset/pmv/daily-logs')}>Daily logs</Button>
            <Button onClick={() => navigate('/asset/pmv/hire/attention')}>
              Commercial attention
            </Button>
          </Space>
        }
        extra={
          <Space wrap>
            <div style={{ minWidth: 200 }}>
              <ProjectSelector
                value={projectId}
                onChange={(v) => setProjectId(v || undefined)}
                allowClear
                placeholder="Project"
              />
            </div>
            <Select
              allowClear
              placeholder="Exception kind"
              style={{ width: 160 }}
              value={kind}
              onChange={setKind}
              options={[
                { value: 'RETURNED', label: 'Returned' },
                { value: 'IN_PROGRESS', label: 'In progress' },
              ]}
            />
          </Space>
        }
      />
      <Alert
        style={{ marginBottom: 12 }}
        type="info"
        showIcon
        message="Sheet exceptions are workflow. Operational truth remains individual daily logs after verify."
      />
      {!loading && rows.length === 0 ? (
        <Empty description="No field ops exceptions for these filters" />
      ) : (
        <Table
          rowKey="id"
          size="small"
          loading={loading}
          dataSource={rows}
          pagination={{ pageSize: 25 }}
          scroll={{ x: 1000 }}
          tableLayout="fixed"
          columns={[
            {
              title: 'Sheet',
              dataIndex: 'sheet_number',
              width: 160,
              ellipsis: true,
              render: (v: string, r) => (
                <div style={hireTableCellEllipsis}>
                  <Button
                    type="link"
                    style={hireTableLinkBtnStyle}
                    onClick={() => navigate(`/asset/pmv/daily-log-sheets/${r.id}`)}
                  >
                    {v}
                  </Button>
                </div>
              ),
            },
            {
              title: 'Kind',
              dataIndex: '_kind',
              width: 130,
              render: (k: string) => (
                <Tag color={k === 'RETURNED' ? 'warning' : 'processing'}>{k}</Tag>
              ),
            },
            {
              title: 'Project',
              dataIndex: 'project_id',
              width: 200,
              ellipsis: true,
              render: (pid: string) => resolveProjectDisplayFromMap(projectLabels, pid),
            },
            { title: 'Date', dataIndex: 'operating_date', width: 120 },
            { title: 'Shift', dataIndex: 'shift_code', width: 90 },
            { title: 'Site', dataIndex: 'site_reference', width: 120, ellipsis: true, render: (v) => v || '—' },
            { title: 'Entries', dataIndex: 'entry_count', width: 90 },
            {
              title: 'Return reason',
              dataIndex: 'return_reason',
              ellipsis: true,
              render: (v) => v || '—',
            },
          ]}
        />
      )}
    </WorkspaceLayout>
  );
};

export default PmvFieldOpsExceptionsPage;
